
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, Sparkles, Star } from 'lucide-react';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { cn } from '@/lib/utils';

// Plan feature list item component
const FeatureItem = ({ included, feature }: { included: boolean; feature: string }) => (
  <div className="flex items-center space-x-2 text-sm py-1">
    {included ? (
      <Check className="h-4 w-4 text-primary" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground" />
    )}
    <span className={cn(!included && "text-muted-foreground")}>{feature}</span>
  </div>
);

interface PlanCardProps {
  name: string;
  title: string;
  description: string;
  price: string;
  features: {
    label: string;
    included: boolean;
  }[];
  isCurrentPlan: boolean;
  onSelect: () => void;
}

const PlanCard = ({ name, title, description, price, features, isCurrentPlan, onSelect }: PlanCardProps) => (
  <Card className={cn("flex flex-col", isCurrentPlan && "border-primary bg-primary/5")}>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="flex-grow">
      <div className="mb-4">
        <span className="text-3xl font-bold">{price}</span>
        <span className="text-sm text-muted-foreground ml-1">/mês</span>
      </div>
      <div className="space-y-1">
        {features.map((feature, i) => (
          <FeatureItem key={i} included={feature.included} feature={feature.label} />
        ))}
      </div>
    </CardContent>
    <CardFooter>
      <Button
        onClick={onSelect}
        variant={isCurrentPlan ? "outline" : "default"} 
        className="w-full"
        disabled={isCurrentPlan}
      >
        {isCurrentPlan ? 'Plano atual' : 'Selecionar plano'}
      </Button>
    </CardFooter>
  </Card>
);

export function PlanSelector() {
  const { userPlan, updateUserPlan } = useFeatureAccess();

  const plans = [
    {
      name: 'basic',
      title: 'Básico',
      description: 'Para agências que estão começando',
      price: 'R$99',
      icon: <Star className="h-5 w-5" />,
      features: [
        { label: 'Dashboard', included: true },
        { label: 'Clientes', included: true },
        { label: 'Fluxo de Trabalho', included: true },
        { label: 'Aprovações', included: true },
        { label: 'Finanças', included: true },
        { label: 'Calendário', included: true }, // Calendar is included in the basic plan
        { label: 'Relatórios', included: false },
        { label: 'Mapa Mental', included: false },
        { label: 'Automações', included: false },
      ]
    },
    {
      name: 'business',
      title: 'Business',
      description: 'Para agências em crescimento',
      price: 'R$199',
      icon: <Sparkles className="h-5 w-5" />,
      features: [
        { label: 'Dashboard', included: true },
        { label: 'Clientes', included: true },
        { label: 'Fluxo de Trabalho', included: true },
        { label: 'Aprovações', included: true },
        { label: 'Finanças', included: true },
        { label: 'Calendário', included: true },
        { label: 'Relatórios', included: true },
        { label: 'Mapa Mental', included: false },
        { label: 'Automações', included: false },
      ]
    },
    {
      name: 'advanced',
      title: 'Advanced',
      description: 'Todas as funcionalidades disponíveis',
      price: 'R$299',
      icon: <Sparkles className="h-5 w-5" />,
      features: [
        { label: 'Dashboard', included: true },
        { label: 'Clientes', included: true },
        { label: 'Fluxo de Trabalho', included: true },
        { label: 'Aprovações', included: true },
        { label: 'Finanças', included: true },
        { label: 'Calendário', included: true },
        { label: 'Relatórios', included: true },
        { label: 'Mapa Mental', included: true },
        { label: 'Automações', included: true },
      ]
    }
  ];

  const handlePlanChange = (planName: 'basic' | 'business' | 'advanced') => {
    updateUserPlan(planName);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Selecione seu plano</h3>
        <p className="text-sm text-muted-foreground">
          Escolha o plano mais adequado para sua agência
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        {plans.map((plan) => (
          <PlanCard
            key={plan.name}
            name={plan.name}
            title={plan.title}
            description={plan.description}
            price={plan.price}
            features={plan.features}
            isCurrentPlan={userPlan === plan.name}
            onSelect={() => handlePlanChange(plan.name as 'basic' | 'business' | 'advanced')}
          />
        ))}
      </div>
    </div>
  );
}
